import React from "react";
import { motion } from "framer-motion";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Zap } from "lucide-react";
import NavBar from "../components/NavBar";
import FooterSection from "../components/FooterSection";
import HeroSection from "../components/HeroSection";
import {
  Breadcrumbs,
  CallToAction,
  DeliverablesAndStandards,
  Faqs,
  Industries,
  LinkGrid,
  PlatformList,
  Process,
  SectionHeading,
  fadeUp,
} from "../components/ServiceSections";
import data from "../data.json";

const Overview = ({ service }) => {
  const paragraphs = service.details?.overview || [service.shortDescription];
  const benefits = service.details?.benefits;

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-6xl flex flex-col lg:flex-row gap-12 items-center">
        <motion.div {...fadeUp} className="w-full lg:w-1/2">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
          {paragraphs.map((paragraph, index) => (
            <p key={index} className="text-lg text-gray-600 mb-4">
              {paragraph}
            </p>
          ))}
          {benefits && (
            <ul className="mt-6 space-y-3">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-custom-blue mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
        <motion.div {...fadeUp} className="w-full lg:w-1/2">
          <div className="relative h-64 lg:h-96 overflow-hidden rounded-lg shadow-lg">
            <img
              src={require(`../assets/images/${service.image}`)}
              alt={service.heading}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const OfferingCard = ({ offering, serviceSlug }) => {
  // Services without detailed content have no image, so show an icon instead
  if (!offering.image) {
    return (
      <motion.div
        {...fadeUp}
        className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100 flex items-center"
      >
        <div className="bg-blue-50 p-3 rounded-full text-custom-blue">
          <Zap className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 ml-4">
          {offering.title}
        </h3>
      </motion.div>
    );
  }

  const content = (
    <>
      <div className="h-48 overflow-hidden">
        <img
          src={require(`../assets/images/${offering.image}`)}
          alt={offering.title}
          loading="lazy"
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-custom-blue transition-colors duration-300">
          {offering.title}
        </h3>
        <p className="text-gray-600">{offering.description}</p>
        {offering.slug && (
          <span className="mt-auto pt-4 inline-flex items-center font-semibold text-custom-blue group-hover:text-custom-red transition-colors duration-300">
            Learn more
            <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        )}
      </div>
    </>
  );

  const cardClassName =
    "group h-full flex flex-col bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100";

  return (
    <motion.div {...fadeUp}>
      {offering.slug ? (
        <RouterLink
          to={`/services/${serviceSlug}/${offering.slug}`}
          className={cardClassName}
        >
          {content}
        </RouterLink>
      ) : (
        <div className={cardClassName}>{content}</div>
      )}
    </motion.div>
  );
};

const Offerings = ({ service }) => {
  // Services without detailed content fall back to their bullet points
  const offerings =
    service.details?.offerings ||
    service.bulletPoints.map((point) => ({ title: point }));

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeading
          title="What We Offer"
          description="Our services cover every stage of your project, from initial assessment to final recommendations."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {offerings.map((offering, index) => (
            <OfferingCard
              key={index}
              offering={offering}
              serviceSlug={service.slug}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = data.servicesList.find((item) => item.slug === slug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const { details } = service;
  const otherServices = data.servicesList
    .filter((item) => item.id !== service.id)
    .map((item) => ({ to: `/services/${item.slug}`, label: item.heading }));

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />

      <HeroSection
        backgroundImage={require(`../assets/images/${service.image}`)}
        heading={service.heading}
        description={service.shortDescription}
      />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: service.heading },
        ]}
      />

      <Overview service={service} />
      <Offerings service={service} />
      {details?.process && <Process steps={details.process} />}
      {details?.industries && <Industries industries={details.industries} />}
      {details?.tools && <PlatformList tools={details.tools} />}
      {details && (
        <DeliverablesAndStandards
          deliverables={details.deliverables}
          standards={details.standards}
        />
      )}
      {details?.faqs && <Faqs faqs={details.faqs} />}
      <CallToAction title={`Need help with ${service.heading}?`} />
      <LinkGrid title="Explore Other Services" links={otherServices} />

      <FooterSection />
    </div>
  );
};

export default ServiceDetail;
